import React, { useMemo } from 'react';
import {
  Assessment as EmissionsIcon,
  Gavel as ComplianceIcon,
  Home as HomeIcon,
  Menu as MoreIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { BottomNavigation, BottomNavigationAction, Paper, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEnterpriseAppPath, shouldShowAppSidebar } from '../constants/appNavigation';
import { isBillUploadEnabled } from '../constants/uiVisibility';
import { useRegistration } from '../context/RegistrationContext';

export const MSME_MOBILE_BOTTOM_NAV_HEIGHT = 64;

type MobileNavItem = {
  label: string;
  path: string;
  icon: typeof HomeIcon;
  match: (pathname: string) => boolean;
};

const enterpriseMobileNavItems = (): MobileNavItem[] => [
  {
    label: 'Home',
    path: '/enterprise/dashboard',
    icon: HomeIcon,
    match: (pathname) => pathname === '/enterprise' || pathname === '/enterprise/dashboard',
  },
  {
    label: 'Data',
    path: '/enterprise/data?section=connectors',
    icon: UploadIcon,
    match: (pathname) => pathname === '/enterprise/data',
  },
  {
    label: 'Footprint',
    path: '/enterprise/carbon-footprint',
    icon: EmissionsIcon,
    match: (pathname) => pathname.startsWith('/enterprise/carbon-footprint'),
  },
  {
    label: 'Compliance',
    path: '/enterprise/compliance',
    icon: ComplianceIcon,
    match: (pathname) =>
      pathname.startsWith('/enterprise/compliance') || pathname.startsWith('/enterprise/reporting'),
  },
  {
    label: 'More',
    path: '/enterprise/settings',
    icon: MoreIcon,
    match: (pathname) =>
      pathname === '/enterprise/settings'
      || pathname === '/enterprise/data-privacy'
      || pathname === '/help',
  },
];

const mobileNavItems = (): MobileNavItem[] => [
  { label: 'Home', path: '/dashboard', icon: HomeIcon, match: (pathname) => pathname === '/dashboard' },
  isBillUploadEnabled()
    ? {
      // Short label fits five bottom-nav actions on ~320px phones; aria-label keeps full name.
      label: 'Upload',
      path: '/document-upload',
      icon: UploadIcon,
      match: (pathname) => pathname === '/document-upload' || pathname === '/data',
    }
    : {
      label: 'Data',
      path: '/data?section=connectors',
      icon: UploadIcon,
      match: (pathname) => pathname === '/data' || pathname === '/document-upload',
    },
  {
    label: 'Footprint',
    path: '/carbon-footprint?section=overview',
    icon: EmissionsIcon,
    match: (pathname) =>
      pathname.startsWith('/carbon-footprint')
      || pathname.startsWith('/carbon-forecasting')
      || pathname.startsWith('/carbon-savings')
      || pathname.startsWith('/emissions-reduction-targets'),
  },
  {
    label: 'Compliance',
    path: '/compliance',
    icon: ComplianceIcon,
    match: (pathname) =>
      pathname.startsWith('/compliance')
      || pathname.startsWith('/reporting')
      || pathname.startsWith('/buyer-audit'),
  },
  {
    label: 'More',
    path: '/settings',
    icon: MoreIcon,
    match: (pathname) =>
      pathname === '/settings'
      || pathname === '/information'
      || pathname === '/help',
  },
];

const MsmeMobileBottomNav: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { isRegistered, isLoggingOut } = useRegistration();
  const navItems = useMemo(
    () => (isEnterpriseAppPath(location.pathname) ? enterpriseMobileNavItems() : mobileNavItems()),
    [location.pathname]
  );

  const showNav = isMobile && !isLoggingOut && shouldShowAppSidebar(location.pathname, isRegistered);

  const activeIndex = useMemo(() => {
    const pathname = location.pathname || '/';
    const index = navItems.findIndex((item) => item.match(pathname));
    return index >= 0 ? index : 0;
  }, [location.pathname, navItems]);

  if (!showNav) {
    return null;
  }

  return (
    <Paper
      component="nav"
      aria-label="Mobile workspace navigation"
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        display: { xs: 'block', md: 'none' },
        borderTop: '1px solid',
        borderColor: 'divider',
        pb: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <BottomNavigation
        value={activeIndex}
        onChange={(_, newValue: number) => {
          const item = navItems[newValue];
          if (item) {
            navigate(item.path);
          }
        }}
        showLabels
        sx={{ height: MSME_MOBILE_BOTTOM_NAV_HEIGHT }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            aria-label={
              item.label === 'Upload'
                ? 'Upload bills'
                : item.label === 'Footprint'
                  ? 'Carbon footprint'
                  : item.label
            }
            icon={<item.icon fontSize="small" />}
            sx={{
              minWidth: 0,
              px: 0.5,
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              },
            }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MsmeMobileBottomNav;
